<script setup lang="ts">
import { ref } from 'vue';
import { onKeyStroke } from '@vueuse/core';

import AppButton from '../../appButton/AppButton.vue';
import AppLazyLoadComponent from '../../appLazyLoadComponent/AppLazyLoadComponent.vue';
import AppImg from '../../appImg/AppImg.vue';
import { KeyboardKeyCode } from '../../../models';

export interface AppFullscreenMasonryGalleryProps {
  imageUrls: string[];
  imagesText: string;
  productText: string;
  closeGalleryAriaLabel: string;
}
const props = defineProps<AppFullscreenMasonryGalleryProps>();

interface AppFullscreenMasonryGalleryEmits {
  (eventName: 'close'): void;
  (eventName: 'image-clicked', index: number): void;
}

const emit = defineEmits<AppFullscreenMasonryGalleryEmits>();

const imageVisibility = ref<boolean[]>(
  Array(props.imageUrls.length).fill(false)
);

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));
</script>

<template>
  <div class="app-fullscreen-masonry-gallery">
    <div class="modal-wrapper">
      <div class="modal-header">
        <div v-if="props.productText" class="product-title">
          <h2 class="title">{{ props.productText }}</h2>
          <span class="images-number">
            {{ props.imageUrls.length }} {{ props.imagesText }}
          </span>
        </div>
        <AppButton
          type="secondary"
          state="enabled"
          size="medium"
          class="gallery-close-button"
          :icon="{ name: 'close' }"
          :aria-label="props.closeGalleryAriaLabel"
          @click.stop="emit('close')"
        />
      </div>
      <div class="modal-content">
        <div class="masonry-gallery">
          <div
            v-for="(image, index) in props.imageUrls"
            :key="index"
            class="images-wrapper"
          >
            <AppLazyLoadComponent
              class="lazy-image"
              @inner-element-visible="imageVisibility[index] = true"
            >
              <AppImg
                :src="image"
                class="image"
                :class="imageVisibility[index] ? 'visible' : 'invisible'"
                :track-time="true"
                alt="image"
                @click="emit('image-clicked', index)"
              />
            </AppLazyLoadComponent>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '../../../styles/_media.scss';
@import '../../../styles/_fonts.scss';

.app-fullscreen-masonry-gallery {
  display: flex;
  flex-direction: column;
  position: fixed;
  z-index: var(--panel-second-level-z-index);
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  overflow: scroll;
  background: var(--color-white);
  overflow-x: hidden;
  padding-left: var(--spacing-16);
  padding-right: var(--spacing-16);

  @include for-more-or-equal-than-1200-px {
    padding-left: 150px;
    padding-right: 150px;
    background: var(--color-soft-black);
  }

  .modal-wrapper {
    display: flex;
    flex-direction: column;
    background: var(--color-white);

    @include for-more-or-equal-than-1200-px {
      border-radius: 0.5rem;
      margin: 10px;
      padding: 0 var(--spacing-24);
    }
  }

  .modal-header {
    position: sticky;
    height: 100%;
    background: var(--color-white);
    top: 0;
    left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: var(--spacing-32);
    padding-top: var(--spacing-24);
    z-index: 4;

    .title {
      @include font-label-xl-bold;
      margin-top: 0;
      margin-bottom: var(--spacing-8);
    }

    .images-number {
      @include font-label-m-light;
    }

    .gallery-close-button {
      align-self: center;
    }
  }

  .modal-content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .masonry-gallery {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 1fr);
    grid-gap: var(--spacing-8);
    overflow: hidden;
    margin-bottom: var(--spacing-16);

    @include for-more-or-equal-than-1200-px {
      grid-template-columns: repeat(3, 1fr);
    }

    .images-wrapper {
      @include for-more-or-equal-than-1200-px {
        height: 20rem;

        &:nth-of-type(12n + 2),
        &:nth-of-type(12n + 7) {
          grid-row: span 2;
          grid-column: span 2;
          height: calc(40rem + var(--spacing-8));
        }
      }
    }
  }

  @keyframes revealAnimation {
    from {
      opacity: 0;
      transform: translateY(100px);
    }
    50% {
      opacity: 0.1;
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    grid-row: span 1;
    border-radius: var(--border-radius-8);
    cursor: pointer;

    &.invisible {
      opacity: 0;
    }
    &.visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      animation: revealAnimation var(--transition-time-very-long) linear
        forwards;
    }
  }

  .lazy-image {
    height: 100%;
  }
}
// NOTE: We are using vw in the border-radius because % results in undesired behaviour depending on the viewport
// px would result in us using magic numbers so vw does the perfect job for both cases
// decision taken when we moved from perfect-scrollbar library to custom design scrollbar
::-webkit-scrollbar-track {
  background-color: var(--color-white);
  border-radius: 100vw;
}

::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 100vw;
}
</style>
